import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDownWideShort,
  faTable,
  faTh,
  faThList,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import cn from "classnames";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  ButtonGroup,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { HISTORY_POST_LOADING } from "../../constants/loading.constant";
import { useGlobalCore } from "../../cores/globals";
import { useRouterCore } from "../../cores/router";
import { useMeCore } from "../../cores/me";
import { useSocialPostCore } from "../../cores/socialPost";
import { SocialHistoryBar } from "../component/socialHistory/SocialHistoryBar";
import { SocialHistoryCard } from "../component/socialHistory/SocialHistoryCard";
import { SocialHistoryTable } from "../component/socialHistory/SocialHistoryTable";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import Layout from "../layout/Layout";
import { useIntl } from "react-intl";
import {
  compareSocialPostsByHistorySort,
  HistorySortKey,
  HistorySortOrder,
} from "../utils/socialHistorySort";
import "./SocialHistory.scss";

type HistoryViewMode = "cards" | "grid" | "table";

export default function SocialHistory() {
  const intl = useIntl();
  const { socialPosts, deleteSocialPost } = useSocialPostCore();
  const { isDarkMode } = useGlobalCore();
  const { goToSocialPost, goToSocial } = useRouterCore();
  const { company, companyRole, isAdmin, isReseller } = useMeCore();

  /** Siempre grilla al entrar; el usuario puede cambiar solo en esta sesión. */
  const [viewMode, setViewMode] = useState<HistoryViewMode>("grid");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const selectAllRef = useRef<HTMLInputElement>(null);
  const [sortKey, setSortKey] = useState<HistorySortKey>("relevant");
  const [sortOrder, setSortOrder] = useState<HistorySortOrder>("desc");
  const [sortModalOpen, setSortModalOpen] = useState(false);

  const sortedPosts = useMemo(() => {
    return [...socialPosts].sort((a, b) =>
      compareSocialPostsByHistorySort(a.post, b.post, sortKey, sortOrder)
    );
  }, [socialPosts, sortKey, sortOrder]);

  const visibleIds = useMemo(
    () => new Set(sortedPosts.map((p) => String(p.post.id))),
    [sortedPosts]
  );

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => visibleIds.has(id)));
  }, [visibleIds]);

  const allVisibleSelected =
    sortedPosts.length > 0 && selectedIds.length === sortedPosts.length;
  const someSelected = selectedIds.length > 0;

  useEffect(() => {
    const el = selectAllRef.current;
    if (el) {
      el.indeterminate = someSelected && !allVisibleSelected;
    }
  }, [someSelected, allVisibleSelected]);

  const toggleSelectOne = useCallback((id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const toggleSelectAllVisible = useCallback(() => {
    if (allVisibleSelected) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(sortedPosts.map((p) => String(p.post.id)));
  }, [allVisibleSelected, sortedPosts]);

  const openBulkDeleteModal = useCallback(() => {
    if (selectedIds.length === 0) return;
    setBulkDeleteModalOpen(true);
  }, [selectedIds.length]);

  const runBulkDelete = useCallback(async () => {
    if (!company?.id || selectedIds.length === 0) return;
    setBulkDeleting(true);
    try {
      for (const id of selectedIds) {
        await deleteSocialPost(company.id, id);
      }
      setSelectedIds([]);
      setBulkDeleteModalOpen(false);
    } finally {
      setBulkDeleting(false);
    }
  }, [company?.id, deleteSocialPost, selectedIds]);

  useEffect(() => {
    if (
      !companyRole?.social &&
      !companyRole?.owner &&
      !companyRole?.admin &&
      !companyRole?.agent &&
      !isAdmin &&
      !isReseller
    )
      goToSocial();
  }, [goToSocial, companyRole, isAdmin, isReseller]);

  return (
    <Layout>
      <Header2 title={intl.formatMessage({ id: "app.sidebar.socialMediaRecord" })} />
      <Content>
        <Container fluid>
          <Row>
            <Col xs="12">
              <SocialHistoryBar />
              <UpdateLoadingCard
                loadingId={HISTORY_POST_LOADING}
                text={intl.formatMessage({ id: "app.social.updating" })}
              />
              <div
                className={cn("social-history-page", {
                  "social-history-page--dark": isDarkMode,
                })}
              >
                <div className="social-history-page__intro">
                  <div className="social-history-page__title-row">
                    <div className="social-history-page__toolbar">
                      {sortedPosts.length > 0 && (
                        <>
                          <span className="social-history-page__stats">
                            {intl.formatMessage(
                              { id: "app.socialHistory.itemsCount" },
                              { count: sortedPosts.length }
                            )}
                          </span>
                          <div className="social-history-page__bulk">
                            <div className="custom-control custom-checkbox social-history-page__bulk-check-wrap">
                              <input
                                ref={selectAllRef}
                                id="social-history-select-all"
                                type="checkbox"
                                className="custom-control-input"
                                checked={allVisibleSelected}
                                onChange={(e) => {
                                  e.preventDefault();
                                  toggleSelectAllVisible();
                                }}
                              />
                              <label
                                className="custom-control-label"
                                htmlFor="social-history-select-all"
                              >
                                {intl.formatMessage({
                                  id: "app.socialHistory.selectVisible",
                                })}
                              </label>
                            </div>
                            {someSelected ? (
                              <span className="social-history-page__bulk-count">
                                {intl.formatMessage(
                                  { id: "app.socialHistory.selectedCount" },
                                  { count: selectedIds.length }
                                )}
                              </span>
                            ) : null}
                            <Button
                              type="button"
                              color="danger"
                              outline
                              size="sm"
                              className="social-history-page__bulk-delete"
                              disabled={!someSelected}
                              onClick={openBulkDeleteModal}
                              title={intl.formatMessage({
                                id: "app.socialHistory.bulkDeleteTitle",
                              })}
                            >
                              <FontAwesomeIcon icon={faTrashAlt} className="mr-1" />
                              <span className="d-none d-sm-inline">
                                {intl.formatMessage({
                                  id: "app.socialHistory.bulkDelete",
                                })}
                              </span>
                              {someSelected ? (
                                <span className="ml-1">({selectedIds.length})</span>
                              ) : null}
                            </Button>
                          </div>
                          <ButtonGroup
                            className="social-history-page__view-toggle"
                            aria-label={intl.formatMessage({
                              id: "app.socialHistory.viewModeLabel",
                            })}
                          >
                            <Button
                              type="button"
                              outline
                              color="secondary"
                              size="sm"
                              active={viewMode === "cards"}
                              aria-pressed={viewMode === "cards"}
                              onClick={() => setViewMode("cards")}
                              title={intl.formatMessage({
                                id: "app.socialHistory.viewCards",
                              })}
                            >
                              <FontAwesomeIcon icon={faThList} className="mr-1" />
                              <span className="d-none d-sm-inline">
                                {intl.formatMessage({ id: "app.socialHistory.viewCards" })}
                              </span>
                            </Button>
                            <Button
                              type="button"
                              outline
                              color="secondary"
                              size="sm"
                              active={viewMode === "grid"}
                              aria-pressed={viewMode === "grid"}
                              onClick={() => setViewMode("grid")}
                              title={intl.formatMessage({
                                id: "app.socialHistory.viewGrid",
                              })}
                            >
                              <FontAwesomeIcon icon={faTh} className="mr-1" />
                              <span className="d-none d-sm-inline">
                                {intl.formatMessage({ id: "app.socialHistory.viewGrid" })}
                              </span>
                            </Button>
                            <Button
                              type="button"
                              outline
                              color="secondary"
                              size="sm"
                              active={viewMode === "table"}
                              aria-pressed={viewMode === "table"}
                              onClick={() => setViewMode("table")}
                              title={intl.formatMessage({
                                id: "app.socialHistory.viewTable",
                              })}
                            >
                              <FontAwesomeIcon icon={faTable} className="mr-1" />
                              <span className="d-none d-sm-inline">
                                {intl.formatMessage({ id: "app.socialHistory.viewTable" })}
                              </span>
                            </Button>
                          </ButtonGroup>
                          <Button
                            type="button"
                            outline
                            color="secondary"
                            size="sm"
                            className="social-history-page__sort-trigger"
                            onClick={() => setSortModalOpen(true)}
                            title={intl.formatMessage({
                              id: "app.socialHistory.sortLabel",
                            })}
                            aria-label={intl.formatMessage({
                              id: "app.socialHistory.sortLabel",
                            })}
                          >
                            <FontAwesomeIcon icon={faArrowDownWideShort} />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  className={cn(
                    "social-history-page__list",
                    sortedPosts.length === 0
                      ? "social-history-page__list--empty"
                      : {
                          "social-history-page__list--cards": viewMode === "cards",
                          "social-history-page__list--grid": viewMode === "grid",
                          "social-history-page__list--table": viewMode === "table",
                        }
                  )}
                >
                  {viewMode === "table" ? (
                    sortedPosts.length > 0 ? (
                      <SocialHistoryTable
                        posts={sortedPosts}
                        isDarkMode={isDarkMode}
                        selectedIds={selectedIds}
                        onToggleSelect={toggleSelectOne}
                        allVisibleSelected={allVisibleSelected}
                        onToggleSelectAll={toggleSelectAllVisible}
                      />
                    ) : null
                  ) : (
                    sortedPosts.map((post) => {
                      const pid = String(post.post.id);
                      return (
                        <SocialHistoryCard
                          key={post.post.id}
                          post={post}
                          isDarkMode={isDarkMode}
                          variant={viewMode === "grid" ? "compact" : "full"}
                          selected={selectedIds.includes(pid)}
                          onToggleSelect={toggleSelectOne}
                        />
                      );
                    })
                  )}
                  {sortedPosts.length === 0 && (
                    <div className="social-history-page__empty">
                      <div className="social-history-page__empty-main">
                        <span className="social-history-page__empty-badge">
                          Asistente IA
                        </span>
                        <h4 className="social-history-page__empty-title">
                          Todavía no hay publicaciones programadas en este rango.
                        </h4>
                        <p className="social-history-page__empty-text">
                          Ajusta filtros o programa un nuevo contenido para activar tu historial.
                        </p>
                      </div>
                      <div className="social-history-page__empty-side">
                        <div className="social-history-page__empty-hints">
                          <span className="social-history-page__empty-hint">Define red y fecha</span>
                          <span className="social-history-page__empty-hint">Elige formato del contenido</span>
                          <span className="social-history-page__empty-hint">Programa y monitorea resultados</span>
                        </div>
                        <Button
                          color="primary"
                          className="social-history-page__empty-cta"
                          onClick={() => goToSocialPost("")}
                        >
                          Programar mi primer posteo
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </Content>
      <Modal
        isOpen={bulkDeleteModalOpen}
        toggle={() => !bulkDeleting && setBulkDeleteModalOpen(false)}
        centered
        className={cn({ "text-dark": !isDarkMode })}
      >
        <ModalHeader toggle={() => !bulkDeleting && setBulkDeleteModalOpen(false)}>
          {intl.formatMessage({ id: "app.socialHistory.bulkDeleteTitle" })}
        </ModalHeader>
        <ModalBody>
          <p className="mb-0">
            {intl.formatMessage(
              { id: "app.socialHistory.bulkDeleteConfirm" },
              { count: selectedIds.length }
            )}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            color="secondary"
            outline
            disabled={bulkDeleting}
            onClick={() => setBulkDeleteModalOpen(false)}
          >
            {intl.formatMessage({ id: "app.socialHistory.bulkDeleteCancel" })}
          </Button>
          <Button
            type="button"
            color="danger"
            disabled={bulkDeleting}
            onClick={() => void runBulkDelete()}
          >
            {bulkDeleting
              ? intl.formatMessage({ id: "app.socialHistory.bulkDeleteWorking" })
              : intl.formatMessage({ id: "app.socialHistory.bulkDeleteConfirmBtn" })}
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        isOpen={sortModalOpen}
        toggle={() => setSortModalOpen(false)}
        centered
        className={cn("social-history-sort-modal", {
          "text-dark": !isDarkMode,
          "social-history-sort-modal--dark": isDarkMode,
        })}
      >
        <ModalHeader toggle={() => setSortModalOpen(false)}>
          {intl.formatMessage({ id: "app.socialHistory.sortModalTitle" })}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label className="font-weight-bold" for="social-history-sort-key-modal">
              {intl.formatMessage({ id: "app.socialHistory.sortLabel" })}
            </Label>
            <Input
              id="social-history-sort-key-modal"
              type="select"
              className="social-history-page__sort-select social-history-page__sort-select--modal"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as HistorySortKey)}
            >
              <option value="relevant">
                {intl.formatMessage({ id: "app.socialHistory.sortRelevant" })}
              </option>
              <option value="published">
                {intl.formatMessage({ id: "app.socialHistory.sortPublished" })}
              </option>
              <option value="scheduled">
                {intl.formatMessage({ id: "app.socialHistory.sortScheduled" })}
              </option>
              <option value="created">
                {intl.formatMessage({ id: "app.socialHistory.sortCreated" })}
              </option>
            </Input>
          </FormGroup>
          <FormGroup className="mb-0">
            <Label className="font-weight-bold" for="social-history-sort-order-modal">
              {intl.formatMessage({ id: "app.socialHistory.sortOrderLabel" })}
            </Label>
            <Input
              id="social-history-sort-order-modal"
              type="select"
              className="social-history-page__sort-select social-history-page__sort-select--modal"
              value={sortOrder}
              onChange={(e) =>
                setSortOrder(e.target.value as HistorySortOrder)
              }
            >
              <option value="desc">
                {intl.formatMessage({ id: "app.socialHistory.sortOrderDesc" })}
              </option>
              <option value="asc">
                {intl.formatMessage({ id: "app.socialHistory.sortOrderAsc" })}
              </option>
            </Input>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            color="primary"
            onClick={() => setSortModalOpen(false)}
          >
            {intl.formatMessage({ id: "app.socialHistory.sortModalDone" })}
          </Button>
        </ModalFooter>
      </Modal>
    </Layout>
  );
}
